if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();

        // Initialize metafield display for the current variant on page load
        this.initializeMetafieldDisplay();

        // Initialize variant info loader
        this.initializeVariantInfoLoader();
        this.updateSkywardsWidget();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true }),
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel"),
            ),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            callback(html);
          })
          .then(async () => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();

            await this.handleVariantSelectionFunction();
            await this.displayCurrentVariantMetafield();
            await this.updateSkywardsWidget();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              // console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]",
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false,
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks,
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false,
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`,
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden"),
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === "",
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden"),
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`,
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`,
          )?.classList.remove("hidden");

          // Ensure SKU row visibility matches content like other metafields
          this.updateSkuVisibility();

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut,
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`,
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`,
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable,
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]"),
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId),
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]"),
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId),
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`,
                  ),
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            },
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`,
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`,
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner",
        ).classList.remove("hidden");
        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner",
            ).classList.add("hidden"),
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`,
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products",
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list",
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`,
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      // Function to handle variant selection and fetch metafields
      async handleVariantSelectionFunction() {
        const STOREFRONT_ACCESS_TOKEN = "a029d832714a77943e1da235921e92d3";
        const STORE_DOMAIN = "lialijewellery.myshopify.com";
        const API_VERSION = "2024-01";

        // Fetch metafields for a product variant
        async function fetchVariantMetafields(variantId, namespace, key) {
          if (!variantId.startsWith("gid://shopify/ProductVariant/")) {
            console.error("Invalid variant ID format:", variantId);
            return null;
          }

          const query = `query($variantId: ID!, $namespace: String!, $key: String!) {
            node(id: $variantId) {
              ... on ProductVariant {
                metafield(namespace: $namespace, key: $key) {
                  id
                  namespace
                  key
                  value
                  type
                }
              }
            }
          }`;

          const variables = { variantId, namespace, key };

          try {
            const response = await fetch(
              `https://${STORE_DOMAIN}/api/${API_VERSION}/graphql.json`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
                },
                body: JSON.stringify({
                  query,
                  variables,
                }),
              },
            );

            const responseData = await response.json();
            const { data, errors } = responseData;

            if (errors) {
              console.error("GraphQL errors:", errors);
              return null;
            }

            if (!data?.node?.metafield) {
              // console.log('Metafield not found for variant:', variantId);
              return null;
            }

            return data.node.metafield;
          } catch (error) {
            console.error("Network error:", error);
            return null;
          }
        }

        // Handle variant selection for both Metal and Size options
        const handleVariantSelection = async (event) => {
          const selectedVariantId = event.target.getAttribute("data-v-id");
          // console.log('Selected variant ID:', selectedVariantId);

          // Show loader when starting to fetch data
          this.showVariantInfoLoader();

          // Define all metafields to fetch
          const metafields = [
            {
              key: "number_of_color_stones",
              displayId: "metafield-display-number-of-color-stones",
            },
            { key: "color", displayId: "metafield-display-color" },
            {
              key: "diamond_color",
              displayId: "metafield-display-diamond-color",
            },
            {
              key: "diamond_clarity",
              displayId: "metafield-display-diamond-clarity",
            },
            { key: "clarity", displayId: "metafield-display-clarity" },
            { key: "carat_look", displayId: "metafield-display-carat-look" },
            { key: "carat", displayId: "metafield-display-carat" },
            { key: "metal", displayId: "metafield-display-metal" },
            { key: "color_stone", displayId: "metafield-display-color-stone" },
            { key: "diamond_cut", displayId: "metafield-display-diamond-cut" },
            { key: "size", displayId: "metafield-display-size" },
            {
              key: "number_of_diamonds",
              displayId: "metafield-display-number-of-diamonds",
            },
            {
              key: "diamond_carat",
              displayId: "metafield-display-diamond-carat",
            },
            {
              key: "steel_weight",
              displayId: "metafield-display-steel-weight",
            },
            { key: "brand", displayId: "metafield-display-brand" },
            {
              key: "metal_weight",
              displayId: "metafield-display-metal-weight",
            },
          ];

          // Clear all previous content
          metafields.forEach((metafield) => {
            const display = document.getElementById(metafield.displayId);
            if (display) {
              display.innerHTML = "";
            }
          });

          let loadedCount = 0;
          const totalMetafields = metafields.length;
          let hasVariantData = false;

          // Fetch and display all metafields
          for (const metafield of metafields) {
            const metafieldData = await fetchVariantMetafields(
              selectedVariantId,
              "custom",
              metafield.key,
            );
            const metafieldDisplay = document.getElementById(
              metafield.displayId,
            );

            if (metafieldDisplay) {
              if (metafieldData && metafieldData.value) {
                // console.log(`Displaying variant ${metafield.key}:`, metafieldData.value);
                metafieldDisplay.innerHTML = `${metafieldData.value}`;
                hasVariantData = true;

                // Show the parent hw-item container for all metafields
                const hwItem = metafieldDisplay.closest(".hw-item");
                if (hwItem) {
                  hwItem.style.display = "grid";
                }
              } else {
                // console.log(`No variant data found for ${metafield.key}`);
                const fallbackValue = this.getProductLevelFallbackValue(
                  metafieldDisplay,
                  metafield.key,
                );
                const hwItem = metafieldDisplay.closest(".hw-item");
                if (fallbackValue) {
                  metafieldDisplay.innerHTML = `${fallbackValue}`;
                  if (hwItem) hwItem.style.display = "grid";
                } else {
                  metafieldDisplay.innerHTML = "";
                  if (hwItem) hwItem.style.display = "none";
                }
              }
              loadedCount++;
            } else {
              // console.log(`Element #${metafield.displayId} not found`);
              loadedCount++;
            }
          }

          // If no variant data found, try product metafields as fallback
          if (!hasVariantData) {
            // console.log('No variant metafields found, trying product metafields as fallback');
            await this.displayProductMetafields();
          } else {
            // Hide loader when all metafields are processed
            if (loadedCount >= totalMetafields) {
              this.hideVariantInfoLoader();
            }
          }
        };

        // Add event listeners to all variant option inputs (both Metal and Size)
        document
          .querySelectorAll(
            '.Hw__product-input, input[name="options[]"], select[name="options[]"]',
          )
          .forEach((input) => {
            input.addEventListener("change", handleVariantSelection);
          });

        // Also listen for variant changes from the main component
        this.addEventListener("variant-change", async (event) => {
          if (event.detail && event.detail.variantId) {
            // console.log('Variant change event received:', event.detail.variantId);
            await this.displayCurrentVariantMetafield();
          }
        });

        // Set initial metafield for the currently selected variant combination
        // Don't set initial data here - let the variant change handler manage it
        // console.log('Variant selection function initialized - waiting for user selection');
      }

      // Skywards widget
      async updateSkywardsWidget() {
        try {
          const geo_response = await fetch(
            `${window.Shopify.routes.root}browsing_context_suggestions.json?country[enabled]=true`,
          );

          const geo_data = await geo_response.json();

          const widget = document.querySelector("#skywards-widget");

          if (widget) {
            widget.style.display =
              geo_data?.detected_values?.country?.handle === "AE"
                ? "flex"
                : "none";
          }

          const tooltipTrigger = document.querySelector(".skywards-tooltip");

          if (tooltipTrigger && !tooltipTrigger.dataset.initialized) {
            tooltipTrigger.dataset.initialized = "true";

            tooltipTrigger.addEventListener("click", function (e) {
              e.stopPropagation();
              this.classList.toggle("active");
            });

            document.addEventListener("click", function () {
              tooltipTrigger.classList.remove("active");
            });
          }
        } catch (error) {
          console.error("Skywards widget error:", error);
        }
      }

      // Method to display metafield for the currently selected variant
      async displayCurrentVariantMetafield() {
        const STOREFRONT_ACCESS_TOKEN = "a029d832714a77943e1da235921e92d3";
        const STORE_DOMAIN = "lialijewellery.myshopify.com";
        const API_VERSION = "2024-01";

        // Show loader when starting to fetch data
        this.showVariantInfoLoader();

        // Get the current variant ID from the form
        const variantInput = this.querySelector('input[name="id"]');
        if (!variantInput || !variantInput.value) {
          // console.log('No variant ID found in form, trying product metafields');
          await this.displayProductMetafields();
          return;
        }

        const currentVariantId = variantInput.value;
        // console.log('Current variant ID from form:', currentVariantId);

        // Convert numeric ID to Shopify GID format if needed
        let shopifyVariantId = currentVariantId;
        if (!currentVariantId.startsWith("gid://shopify/ProductVariant/")) {
          shopifyVariantId = `gid://shopify/ProductVariant/${currentVariantId}`;
        }

        // Define all metafields to fetch
        const metafields = [
          {
            key: "number_of_color_stones",
            displayId: "metafield-display-number-of-color-stones",
          },
          { key: "color", displayId: "metafield-display-color" },
          {
            key: "diamond_color",
            displayId: "metafield-display-diamond-color",
          },
          {
            key: "diamond_clarity",
            displayId: "metafield-display-diamond-clarity",
          },
          { key: "clarity", displayId: "metafield-display-clarity" },
          { key: "carat_look", displayId: "metafield-display-carat-look" },
          { key: "carat", displayId: "metafield-display-carat" },
          { key: "metal", displayId: "metafield-display-metal" },
          { key: "color_stone", displayId: "metafield-display-color-stone" },
          { key: "diamond_cut", displayId: "metafield-display-diamond-cut" },
          { key: "size", displayId: "metafield-display-size" },
          {
            key: "number_of_diamonds",
            displayId: "metafield-display-number-of-diamonds",
          },
          {
            key: "diamond_carat",
            displayId: "metafield-display-diamond-carat",
          },
          { key: "steel_weight", displayId: "metafield-display-steel-weight" },
          { key: "brand", displayId: "metafield-display-brand" },
          { key: "metal_weight", displayId: "metafield-display-metal-weight" },
        ];

        let loadedCount = 0;
        const totalMetafields = metafields.length;
        let hasVariantData = false;

        // Fetch and display all metafields
        for (const metafield of metafields) {
          const metafieldData = await this.fetchVariantMetafields(
            shopifyVariantId,
            "custom",
            metafield.key,
          );
          const metafieldDisplay = document.getElementById(metafield.displayId);

          if (metafieldDisplay) {
            if (metafieldData && metafieldData.value) {
              // console.log(`Displaying variant ${metafield.key}:`, metafieldData.value);
              metafieldDisplay.innerHTML = `${metafieldData.value}`;
              hasVariantData = true;

              // Show the parent hw-item container for all metafields
              const hwItem = metafieldDisplay.closest(".hw-item");
              if (hwItem) {
                hwItem.style.display = "grid";
              }
            } else {
              // console.log(`No variant data found for ${metafield.key}`);
              const fallbackValue = this.getProductLevelFallbackValue(
                metafieldDisplay,
                metafield.key,
              );
              const hwItem = metafieldDisplay.closest(".hw-item");
              if (fallbackValue) {
                metafieldDisplay.innerHTML = `${fallbackValue}`;
                if (hwItem) hwItem.style.display = "grid";
              } else {
                metafieldDisplay.innerHTML = "";
                if (hwItem) hwItem.style.display = "none";
              }
            }
            loadedCount++;
          } else {
            // console.log(`Element #${metafield.displayId} not found`);
            loadedCount++;
          }
        }

        // If no variant data found, try product metafields as fallback
        if (!hasVariantData) {
          // console.log('No variant metafields found, trying product metafields as fallback');
          await this.displayProductMetafields();
        } else {
          // Hide loader when all metafields are processed
          if (loadedCount >= totalMetafields) {
            this.hideVariantInfoLoader();
          }
        }
      }

      // Method to initialize metafield display on page load
      async initializeMetafieldDisplay() {
        // Wait a bit for the DOM to be fully ready
        setTimeout(async () => {
          try {
            // Show loader when starting initialization
            this.showVariantInfoLoader();

            // First try to get variant ID from the form
            const variantInput = this.querySelector('input[name="id"]');
            if (variantInput && variantInput.value) {
              // console.log('Initializing metafield display from form variant ID');
              await this.displayCurrentVariantMetafield();
              // Also ensure SKU visibility is correct on initial load
              this.updateSkuVisibility();
              return;
            }

            // If no form variant ID, try to get from URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            const variantParam = urlParams.get("variant");
            if (variantParam) {
              // console.log('Initializing metafield display from URL variant parameter:', variantParam);
              const shopifyVariantId = `gid://shopify/ProductVariant/${variantParam}`;
              const metafield = await this.fetchVariantMetafields(
                shopifyVariantId,
                "custom",
                "carat_look",
              );
              const metafieldDisplay =
                document.getElementById("metafield-display");

              if (metafieldDisplay && metafield && metafield.value) {
                // console.log('Displaying initial metafield value from URL:', metafield.value);
                metafieldDisplay.innerHTML = `<p>Metafield value: ${metafield.value}</p>`;
              }
              this.hideVariantInfoLoader();
              this.updateSkuVisibility();
              return;
            }

            // If no URL variant, try to get from the first selected radio button
            const firstSelectedRadio = document.querySelector(
              ".Hw__product-input:checked",
            );
            if (firstSelectedRadio) {
              const initialVariantId =
                firstSelectedRadio.getAttribute("data-v-id");
              if (initialVariantId) {
                // console.log('Initializing metafield display from selected radio button:', initialVariantId);

                // Define all metafields to fetch
                const metafields = [
                  {
                    key: "number_of_color_stones",
                    displayId: "metafield-display-number-of-color-stones",
                  },
                  { key: "color", displayId: "metafield-display-color" },
                  {
                    key: "diamond_color",
                    displayId: "metafield-display-diamond-color",
                  },
                  {
                    key: "diamond_clarity",
                    displayId: "metafield-display-diamond-clarity",
                  },
                  { key: "clarity", displayId: "metafield-display-clarity" },
                  {
                    key: "carat_look",
                    displayId: "metafield-display-carat-look",
                  },
                  { key: "carat", displayId: "metafield-display-carat" },
                  { key: "metal", displayId: "metafield-display-metal" },
                  {
                    key: "color_stone",
                    displayId: "metafield-display-color-stone",
                  },
                  {
                    key: "diamond_cut",
                    displayId: "metafield-display-diamond-cut",
                  },
                  { key: "size", displayId: "metafield-display-size" },
                  {
                    key: "number_of_diamonds",
                    displayId: "metafield-display-number-of-diamonds",
                  },
                  {
                    key: "diamond_carat",
                    displayId: "metafield-display-diamond-carat",
                  },
                  {
                    key: "steel_weight",
                    displayId: "metafield-display-steel-weight",
                  },
                  { key: "brand", displayId: "metafield-display-brand" },
                  {
                    key: "metal_weight",
                    displayId: "metafield-display-metal-weight",
                  },
                ];

                let loadedCount = 0;
                const totalMetafields = metafields.length;
                let hasVariantData = false;

                // Fetch and display all metafields
                for (const metafield of metafields) {
                  const metafieldData = await this.fetchVariantMetafields(
                    initialVariantId,
                    "custom",
                    metafield.key,
                  );
                  const metafieldDisplay = document.getElementById(
                    metafield.displayId,
                  );

                  if (metafieldDisplay) {
                    if (metafieldData && metafieldData.value) {
                      // console.log(`Displaying initial variant ${metafield.key}:`, metafieldData.value);
                      metafieldDisplay.innerHTML = `${metafieldData.value}`;
                      hasVariantData = true;

                      // Show the parent hw-item container for all metafields
                      const hwItem = metafieldDisplay.closest(".hw-item");
                      if (hwItem) {
                        hwItem.style.display = "grid";
                      }
                    } else {
                      const fallbackValue = this.getProductLevelFallbackValue(
                        metafieldDisplay,
                        metafield.key,
                      );
                      const hwItem = metafieldDisplay.closest(".hw-item");
                      if (fallbackValue) {
                        metafieldDisplay.innerHTML = `${fallbackValue}`;
                        if (hwItem) hwItem.style.display = "grid";
                      } else {
                        metafieldDisplay.innerHTML = "";
                        if (hwItem) hwItem.style.display = "none";
                      }
                    }
                    loadedCount++;
                  } else {
                    loadedCount++;
                  }
                }

                // If no variant data found, try product metafields as fallback
                if (!hasVariantData) {
                  // console.log('No initial variant metafields found, trying product metafields as fallback');
                  await this.displayProductMetafields();
                } else {
                  // Hide loader when all metafields are processed
                  if (loadedCount >= totalMetafields) {
                    this.hideVariantInfoLoader();
                  }
                }
                this.updateSkuVisibility();
                return;
              }
            }

            // console.log('No initial variant found for metafield display, trying product metafields as fallback');
            await this.displayProductMetafields();
          } catch (error) {
            console.error("Error initializing metafield display:", error);
            this.hideVariantInfoLoader();
          }
        }, 100); // Small delay to ensure DOM is ready
      }

      // Fallback to product-level value for selected metafields only.
      getProductLevelFallbackValue(metafieldDisplay, metafieldKey) {
        const fallbackKeys = new Set(["diamond_color", "diamond_clarity"]);
        if (!fallbackKeys.has(metafieldKey)) return "";
        return (metafieldDisplay?.dataset?.productMetafield || "").trim();
      }

      // Toggle SKU row like other metafields based on content presence
      updateSkuVisibility() {
        const skuDisplay = document.getElementById(
          `Sku-${this.dataset.section}`,
        );
        if (!skuDisplay) return;
        const hwItem = skuDisplay.closest(".hw-item");
        if (!hwItem) return;
        const text = (skuDisplay.textContent || "").trim();
        hwItem.style.display = text ? "grid" : "none";
      }

      // Helper method to fetch metafields
      async fetchVariantMetafields(variantId, namespace, key) {
        const STOREFRONT_ACCESS_TOKEN = "a029d832714a77943e1da235921e92d3";
        const STORE_DOMAIN = "lialijewellery.myshopify.com";
        const API_VERSION = "2024-01";

        if (!variantId.startsWith("gid://shopify/ProductVariant/")) {
          console.error("Invalid variant ID format:", variantId);
          return null;
        }

        const query = `query($variantId: ID!, $namespace: String!, $key: String!) {
          node(id: $variantId) {
            ... on ProductVariant {
              metafield(namespace: $namespace, key: $key) {
                id
                namespace
                key
                value
                type
              }
            }
          }
        }`;

        const variables = { variantId, namespace, key };

        try {
          const response = await fetch(
            `https://${STORE_DOMAIN}/api/${API_VERSION}/graphql.json`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
              },
              body: JSON.stringify({
                query,
                variables,
              }),
            },
          );

          const responseData = await response.json();
          const { data, errors } = responseData;

          if (errors) {
            console.error("GraphQL errors:", errors);
            return null;
          }

          if (!data?.node?.metafield) {
            // console.log('Metafield not found for variant:', variantId);
            return null;
          }

          return data.node.metafield;
        } catch (error) {
          console.error("Network error:", error);
          return null;
        }
      }

      // Helper method to fetch product metafields
      async fetchProductMetafields(productId, namespace, key) {
        const STOREFRONT_ACCESS_TOKEN = "a029d832714a77943e1da235921e92d3";
        const STORE_DOMAIN = "lialijewellery.myshopify.com";
        const API_VERSION = "2024-01";

        if (!productId.startsWith("gid://shopify/Product/")) {
          console.error("Invalid product ID format:", productId);
          return null;
        }

        const query = `query($productId: ID!, $namespace: String!, $key: String!) {
          node(id: $productId) {
            ... on Product {
              metafield(namespace: $namespace, key: $key) {
                id
                namespace
                key
                value
                type
              }
            }
          }
        }`;

        const variables = { productId, namespace, key };

        try {
          const response = await fetch(
            `https://${STORE_DOMAIN}/api/${API_VERSION}/graphql.json`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
              },
              body: JSON.stringify({
                query,
                variables,
              }),
            },
          );

          const responseData = await response.json();
          const { data, errors } = responseData;

          if (errors) {
            console.error("GraphQL errors:", errors);
            return null;
          }

          if (!data?.node?.metafield) {
            // console.log('Metafield not found for product:', productId);
            return null;
          }

          return data.node.metafield;
        } catch (error) {
          console.error("Network error:", error);
          return null;
        }
      }

      // Method to display product metafields as fallback
      async displayProductMetafields() {
        const STOREFRONT_ACCESS_TOKEN = "a029d832714a77943e1da235921e92d3";
        const STORE_DOMAIN = "lialijewellery.myshopify.com";
        const API_VERSION = "2024-01";

        // Get product ID from the current page URL or product data
        let productId = null;

        // Try to get product ID from the page data
        const productData = window.Shopify?.analytics?.meta?.product;
        if (productData && productData.id) {
          productId = `gid://shopify/Product/${productData.id}`;
        } else {
          // Try to get from URL or other sources
          const currentUrl = window.location.pathname;
          const productHandle = currentUrl.split("/products/")[1];
          if (productHandle) {
            // We need to fetch the product by handle to get the ID
            const productQuery = `query($handle: String!) {
              product(handle: $handle) {
                id
              }
            }`;

            try {
              const response = await fetch(
                `https://${STORE_DOMAIN}/api/${API_VERSION}/graphql.json`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "X-Shopify-Storefront-Access-Token":
                      STOREFRONT_ACCESS_TOKEN,
                  },
                  body: JSON.stringify({
                    query: productQuery,
                    variables: { handle: productHandle },
                  }),
                },
              );

              const responseData = await response.json();
              if (responseData.data?.product?.id) {
                productId = responseData.data.product.id;
              }
            } catch (error) {
              console.error("Error fetching product ID:", error);
            }
          }
        }

        if (!productId) {
          // console.log('No product ID found for metafield fallback');
          this.hideVariantInfoLoader();
          return;
        }

        // console.log('Using product ID for metafield fallback:', productId);

        // Define all metafields to fetch
        const metafields = [
          {
            key: "number_of_color_stones",
            displayId: "metafield-display-number-of-color-stones",
          },
          { key: "color", displayId: "metafield-display-color" },
          {
            key: "diamond_color",
            displayId: "metafield-display-diamond-color",
          },
          {
            key: "diamond_clarity",
            displayId: "metafield-display-diamond-clarity",
          },
          { key: "clarity", displayId: "metafield-display-clarity" },
          { key: "carat_look", displayId: "metafield-display-carat-look" },
          { key: "carat", displayId: "metafield-display-carat" },
          { key: "metal", displayId: "metafield-display-metal" },
          { key: "color_stone", displayId: "metafield-display-color-stone" },
          { key: "diamond_cut", displayId: "metafield-display-diamond-cut" },
          { key: "size", displayId: "metafield-display-size" },
          {
            key: "number_of_diamonds",
            displayId: "metafield-display-number-of-diamonds",
          },
          {
            key: "diamond_carat",
            displayId: "metafield-display-diamond-carat",
          },
          { key: "steel_weight", displayId: "metafield-display-steel-weight" },
          { key: "brand", displayId: "metafield-display-brand" },
          { key: "metal_weight", displayId: "metafield-display-metal-weight" },
        ];

        let loadedCount = 0;
        const totalMetafields = metafields.length;

        // Fetch and display all product metafields
        for (const metafield of metafields) {
          const metafieldData = await this.fetchProductMetafields(
            productId,
            "custom",
            metafield.key,
          );
          const metafieldDisplay = document.getElementById(metafield.displayId);

          if (metafieldDisplay) {
            if (metafieldData && metafieldData.value) {
              // console.log(`Displaying product ${metafield.key}:`, metafieldData.value);
              metafieldDisplay.innerHTML = `${metafieldData.value}`;

              // Show the parent hw-item container for all metafields
              const hwItem = metafieldDisplay.closest(".hw-item");
              if (hwItem) {
                hwItem.style.display = "grid";
              }
            } else {
              // console.log(`No product data found for ${metafield.key}`);
              metafieldDisplay.innerHTML = "";

              // Hide the parent hw-item container for all metafields when no data
              const hwItem = metafieldDisplay.closest(".hw-item");
              if (hwItem) {
                hwItem.style.display = "none";
              }
            }
            loadedCount++;
          } else {
            // console.log(`Element #${metafield.displayId} not found`);
            loadedCount++;
          }
        }

        // Hide loader when all metafields are processed
        if (loadedCount >= totalMetafields) {
          this.hideVariantInfoLoader();
        }
      }

      // Initialize variant info loader functionality
      initializeVariantInfoLoader() {
        this.variantInfoLoader = document.getElementById("variant-info-loader");
        this.variantInfoContent = document.querySelector(
          ".hw-product--varientInfo",
        );

        if (this.variantInfoLoader) {
          // Show loader initially
          this.showVariantInfoLoader();
        }
      }

      // Show the variant info loader
      showVariantInfoLoader() {
        if (this.variantInfoLoader) {
          this.variantInfoLoader.style.display = "flex";
        }
      }

      // Hide the variant info loader
      hideVariantInfoLoader() {
        if (this.variantInfoLoader) {
          this.variantInfoLoader.style.display = "none";
        }
      }
    },
  );
}
